/**
 * How the hub names an author, and the channel they authored through.
 *
 * Mirrors `orchestration/functions/src/services/skills/skillAuthor.ts` — the API stamps the fields,
 * this reads them. Kept as a separate copy rather than a shared package because the two builds do
 * not share a tsconfig; if the stamp gains a field, both files change together.
 *
 * The rules exist because of what the hub used to show. A guest account saving through SkillBuilder
 * was written with only its Entra object ID, and the card rendered that ID as if it were a person —
 * "35b2a32e-7ace-41f2-8446-d864b83f20a9", truncated to "35b2a32e…" by the column width. Nobody can
 * tell who that is, and the truncation hid even the fact that it was a GUID.
 */

/** The surface a skill was written through. */
export type AuthoringChannel = 'hub' | 'skillbuilder';

export const CHANNEL_LABELS: Record<AuthoringChannel, string> = {
  hub: 'Skills Hub',
  skillbuilder: 'SkillBuilder',
};

/** The authorship fields a skill record can carry. All optional — records predate the stamp. */
export interface SkillAuthorFields {
  createdBy?: string;
  createdByOid?: string;
  createdByName?: string;
  createdVia?: string;
  updatedBy?: string;
  updatedByOid?: string;
  updatedByName?: string;
  updatedVia?: string;
}

/**
 * The name to show for an author: their display name, else their address, else an explicit
 * statement that the account did not resolve.
 *
 * A bare object ID is never presented as an identity. Showing it invites the reader to treat a GUID
 * as a person and, worse, to believe the hub knows who acted when it does not.
 */
export function authorLabel(record: SkillAuthorFields | null | undefined, which: 'created' | 'updated' = 'created'): string {
  if (!record) return 'Unknown';
  const name = (record[`${which}ByName`] ?? '').trim();
  if (name) return name;
  const by = (record[`${which}By`] ?? '').trim();
  if (!by || by === 'unknown') return 'Unknown';
  if (by.includes('@')) return by;
  return `Unresolved account (${by.slice(0, 8)}…)`;
}

/**
 * How the skill got here — "Skills Hub" or "SkillBuilder" — or null for records written before the
 * channel was recorded. Null renders as nothing rather than as a guess: the two paths had the same
 * shape until now, so there is no way to infer which one an old record came through.
 */
export function channelLabel(record: SkillAuthorFields | null | undefined, which: 'created' | 'updated' = 'created'): string | null {
  const via = record?.[`${which}Via`];
  if (via === 'hub' || via === 'skillbuilder') return CHANNEL_LABELS[via];
  return null;
}

/** Full attribution for a tooltip: the address behind the name, and the channel. */
export function authorTitle(record: SkillAuthorFields | null | undefined, which: 'created' | 'updated' = 'created'): string {
  if (!record) return '';
  const parts = [authorLabel(record, which)];
  const by = (record[`${which}By`] ?? '').trim();
  if (by && by !== 'unknown' && by !== parts[0]) parts.push(by);
  const via = channelLabel(record, which);
  if (via) parts.push(`via ${via}`);
  return parts.join(' · ');
}
